import { computed, ref, watch, type Ref } from 'vue'

const observeWidth = (
  elRef: Ref<HTMLElement | undefined>,
  widthRef: Ref<number>
) => {
  watch(elRef, (el, _, onCleanup) => {
    if (!el) {
      widthRef.value = 0
      return
    }
    // offsetWidth: the pill's padding counts toward how far it can travel.
    const observer = new ResizeObserver(() => {
      widthRef.value = el.offsetWidth
    })
    observer.observe(el)
    onCleanup(() => observer.disconnect())
  })
}

/**
 * Positions a location pill centered on `centerPct` of its container, but
 * clamped so it never pokes past either edge. `pillCenterPct` is where the
 * pill actually landed, for the bracket stem to meet; null until measured.
 * Until both widths are known the pill falls back to plain centering.
 */
export const useClampedPill = (
  containerEl: Ref<HTMLElement | undefined>,
  centerPct: Ref<number>
) => {
  const pillEl = ref<HTMLElement>()
  const containerWidth = ref(0)
  const pillWidth = ref(0)
  observeWidth(containerEl, containerWidth)
  observeWidth(pillEl, pillWidth)

  const measured = computed(
    () => containerWidth.value > 0 && pillWidth.value > 0
  )

  const leftPx = computed(() => {
    const center = (centerPct.value / 100) * containerWidth.value
    return Math.max(
      0,
      Math.min(
        center - pillWidth.value / 2,
        containerWidth.value - pillWidth.value
      )
    )
  })

  const pillStyle = computed(() =>
    measured.value
      ? { left: `${leftPx.value}px` }
      : { left: `${centerPct.value}%`, transform: 'translateX(-50%)' }
  )

  const pillCenterPct = computed(() =>
    measured.value
      ? ((leftPx.value + pillWidth.value / 2) / containerWidth.value) * 100
      : null
  )

  return { pillEl, pillStyle, pillCenterPct }
}
